import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import BarChartIcon from '@mui/icons-material/BarChart';
import PageHeader from '../components/layout/PageHeader';
import GuestList from '../components/guests/GuestList';
import GuestForm from '../components/guests/GuestForm';
import CategoryManager from '../components/guests/CategoryManager';
import GuestBreakdown from '../components/guests/GuestBreakdown';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import usePageTitle from '../hooks/usePageTitle';
import { Guest, GuestFormData, getGuests, createGuest, updateGuest, deleteGuest, exportGuestsCsv } from '../services/guests';
import { GuestCategory, getGuestCategories } from '../services/guestCategories';

const GuestsPage: React.FC = () => {
  usePageTitle('Guests');
  const [guests, setGuests] = useState<Guest[]>([]);
  const [categories, setCategories] = useState<GuestCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Guest | null>(null);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Bulk category change
  const [bulkCatAnchor, setBulkCatAnchor] = useState<HTMLElement | null>(null);
  const [bulkCatField, setBulkCatField] = useState<'category_level1_id' | 'category_level2_id'>('category_level1_id');

  // Bulk delete confirm
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [cat1Filter, setCat1Filter] = useState('');
  const [cat2Filter, setCat2Filter] = useState('');

  const loadCategories = async () => {
    try {
      const res = await getGuestCategories();
      setCategories(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadGuests = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (cat1Filter) params.category_level1_id = cat1Filter;
      if (cat2Filter) params.category_level2_id = cat2Filter;
      const res = await getGuests(params);
      setGuests(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, cat1Filter, cat2Filter]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadGuests();
  }, [loadGuests]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, cat1Filter, cat2Filter]);

  const handleSave = async (data: GuestFormData) => {
    try {
      if (editingGuest) {
        await updateGuest(editingGuest.id, data);
      } else {
        await createGuest(data);
      }
      setFormOpen(false);
      setEditingGuest(null);
      loadGuests();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteGuest(deleteTarget.id);
      setDeleteTarget(null);
      loadGuests();
    } catch (err) {
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(Array.from(selectedIds).map((id) => deleteGuest(id)));
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      loadGuests();
    } catch (err) {
      console.error(err);
    }
  };

  const handleBulkCategoryChange = async (categoryId: string | null) => {
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => updateGuest(id, { [bulkCatField]: categoryId })),
      );
      setBulkCatAnchor(null);
      setSelectedIds(new Set());
      loadGuests();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCategoryChange = async (
    guest: Guest,
    field: 'category_level1_id' | 'category_level2_id',
    categoryId: string | null,
  ) => {
    try {
      await updateGuest(guest.id, { [field]: categoryId });
      loadGuests();
    } catch (err) {
      console.error(err);
    }
  };

  const handleExport = async () => {
    try {
      let res;
      if (selectedIds.size > 0) {
        // Export selected: fetch all, filter client-side, build CSV
        // Simpler: just export with current filters (server-side) — but for selected we need client-side
        const selectedGuests = guests.filter((g) => selectedIds.has(g.id));
        const headers = ['First Name', 'Last Name', 'Phone', 'Email', 'Invited', 'Expected'];
        const rows = selectedGuests.map((g) =>
          [g.first_name, g.last_name, g.phone || '', g.email || '', g.num_invited, g.num_expected_to_attend].join(','),
        );
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'guests.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const params: Record<string, string> = {};
        if (cat1Filter) params.category_level1_id = cat1Filter;
        if (cat2Filter) params.category_level2_id = cat2Filter;
        res = await exportGuestsCsv(params);
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'guests.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const level1Cats = categories.filter((c) => c.level === 1);
  const level2Cats = categories.filter((c) => c.level === 2);
  const hasSelection = selectedIds.size > 0;

  const bulkCatOptions = bulkCatField === 'category_level1_id' ? level1Cats : level2Cats;

  return (
    <>
      <PageHeader
        title="Guests"
        actionLabel="Add Guest"
        onAction={() => { setEditingGuest(null); setFormOpen(true); }}
      />
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search guests..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: 1, sm: '0 0 auto' } }}>
          <InputLabel>Category</InputLabel>
          <Select value={cat1Filter} label="Category" onChange={(e) => setCat1Filter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {level1Cats.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: 1, sm: '0 0 auto' } }}>
          <InputLabel>Sub Category</InputLabel>
          <Select value={cat2Filter} label="Sub Category" onChange={(e) => setCat2Filter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {level2Cats.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={() => setCategoryManagerOpen(true)}
            sx={{ flex: { xs: 1, sm: '0 0 auto' } }}
          >
            Categories
          </Button>
          <Button
            variant="outlined"
            startIcon={<BarChartIcon />}
            onClick={() => setBreakdownOpen(true)}
            sx={{ flex: { xs: 1, sm: '0 0 auto' } }}
          >
            Breakdown
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ flex: { xs: 1, sm: '0 0 auto' }, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {hasSelection ? `Export (${selectedIds.size})` : 'Export'}
          </Button>
        </Box>
      </Box>

      {loading ? <LoadingSpinner /> : (
        <GuestList
          guests={guests}
          categories={categories}
          loading={false}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onEdit={(g) => { setEditingGuest(g); setFormOpen(true); }}
          onCategoryChange={handleCategoryChange}
        />
      )}

      {/* Selection action bar */}
      {hasSelection && (
        <Box sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          alignItems: 'center',
          bgcolor: '#B07A8F15',
          border: '1px solid',
          borderColor: '#B07A8F40',
          borderRadius: 2,
          px: 2,
          py: 1,
        }}>
          <Chip label={`${selectedIds.size} selected`} size="small" color="primary" />
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setBulkDeleteOpen(true)}
            sx={{ color: 'error.main' }}
          >
            Delete
          </Button>
          <Button
            size="small"
            onClick={(e) => { setBulkCatField('category_level1_id'); setBulkCatAnchor(e.currentTarget); }}
          >
            Set Category
          </Button>
          <Button
            size="small"
            onClick={(e) => { setBulkCatField('category_level2_id'); setBulkCatAnchor(e.currentTarget); }}
          >
            Set Sub Category
          </Button>
          <Button
            size="small"
            onClick={() => setSelectedIds(new Set())}
            sx={{ ml: 'auto', color: 'grey.600' }}
          >
            Clear
          </Button>
        </Box>
      )}

      <GuestForm
        open={formOpen}
        guest={editingGuest}
        categories={categories}
        onSave={handleSave}
        onDelete={(g) => { setFormOpen(false); setEditingGuest(null); setDeleteTarget(g); }}
        onClose={() => { setFormOpen(false); setEditingGuest(null); }}
      />
      <CategoryManager
        open={categoryManagerOpen}
        onClose={() => setCategoryManagerOpen(false)}
        onChanged={() => { loadCategories(); loadGuests(); }}
      />
      <GuestBreakdown
        open={breakdownOpen}
        guests={guests}
        categories={categories}
        onClose={() => setBreakdownOpen(false)}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Guest"
        message={`Are you sure you want to delete ${deleteTarget?.first_name} ${deleteTarget?.last_name}?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        title="Delete Selected Guests"
        message={`Are you sure you want to delete ${selectedIds.size} selected guest${selectedIds.size !== 1 ? 's' : ''}? This cannot be undone.`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      {/* Bulk category picker menu */}
      <Menu
        anchorEl={bulkCatAnchor}
        open={!!bulkCatAnchor}
        onClose={() => setBulkCatAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkCategoryChange(null)}>
          <Chip label="None" size="small" variant="outlined" />
        </MenuItem>
        {bulkCatOptions.map((cat) => (
          <MenuItem key={cat.id} onClick={() => handleBulkCategoryChange(cat.id)}>
            <Chip
              label={cat.name}
              size="small"
              sx={{
                bgcolor: cat.color ? `${cat.color}25` : 'grey.100',
                color: cat.color || 'grey.800',
                fontWeight: 500,
                border: `1px solid ${cat.color || '#E4DED8'}`,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default GuestsPage;
